/**
 * Wire-format schema for `wp/v2/ezcategory`.
 *
 * Same pattern as the other wire schemas — parse the raw envelope
 * from WP, transform into our camelCase `Category` entity. WP returns
 * many extra fields per term; we pick what we need.
 */
import { z } from 'zod'

import { type Category } from '@/entities/category'
import { htmlToPlain } from '@/shared/lib/html'

const CategoryWireSchema = z
  .object({
    id: z.number().int().positive(),
    name: z.string(),
    slug: z.string(),
    description: z.string().optional().default(''),
    parent: z.number().int().nonnegative().default(0),
    count: z.number().int().nonnegative().default(0),
    // Carbon Fields top-level surfacing — same pattern as `crb_dish_price`,
    // `crb_price`, `crb_tag_color`. Not in OPTIONS schema but always
    // present in the response. Default to base/empty so legacy / unset
    // rows don't trip parsing.
    crb_menu_category_type: z.string().optional().default('base'),
    crb_menu_category_color: z.string().optional().default(''),
    // `term_order` is a top-level integer surfaced by ezqr-core's
    // taxonomy hooks — NOT in `meta`. Default 0 keeps legacy responses
    // (sites still on plugin < 0.6) parseable.
    term_order: z.number().int().nonnegative().optional().default(0),
  })
  .transform(
    (raw): Category => ({
      id: raw.id,
      name: htmlToPlain(raw.name),
      slug: raw.slug,
      description: htmlToPlain(raw.description ?? ''),
      parentId: raw.parent,
      count: raw.count,
      kind: raw.crb_menu_category_type === 'accent' ? 'accent' : 'base',
      color: raw.crb_menu_category_color,
      termOrder: raw.term_order,
    }),
  )

export const categoryWire = CategoryWireSchema
export const categoryListWire = z.array(CategoryWireSchema)
